import { create } from 'zustand';
import { api, setAccessToken } from './api-client.js';

/**
 * Platform-console session. Kept separate from the company session so the two
 * scopes never mix (SRS §3): a platform token can never be used as a company
 * token, and the stores cannot be confused for one another in a component.
 */
export interface PlatformUser {
  id: string;
  email: string;
  fullName: string;
  role: 'SUPER_ADMIN' | 'SUPPORT' | 'BILLING';
  twoFactorOn: boolean;
}

interface PlatformState {
  user: PlatformUser | null;
  status: 'unknown' | 'anonymous' | 'authenticated';
  hydrate: () => Promise<void>;
  setSession: (payload: { accessToken: string; user: PlatformUser }) => void;
  signOut: () => Promise<void>;
  is: (...roles: PlatformUser['role'][]) => boolean;
}

export const usePlatformAuth = create<PlatformState>((set, get) => ({
  user: null,
  status: 'unknown',

  is: (...roles) => {
    const role = get().user?.role;
    return role !== undefined && roles.includes(role);
  },

  hydrate: async () => {
    try {
      // There is no platform /me endpoint; the refresh returns who we are.
      const refreshed = await api<{ accessToken: string; user: PlatformUser }>('/auth/refresh', {
        method: 'POST',
        scope: 'platform',
        anonymous: true,
      });
      setAccessToken(refreshed.accessToken, 'platform');
      set({ status: 'authenticated', user: refreshed.user });
    } catch {
      set({ user: null, status: 'anonymous' });
    }
  },

  setSession: ({ accessToken, user }) => {
    setAccessToken(accessToken, 'platform');
    set({ user, status: 'authenticated' });
  },

  signOut: async () => {
    await api('/auth/logout', { method: 'POST', scope: 'platform' }).catch(() => undefined);
    setAccessToken(null, 'platform');
    set({ user: null, status: 'anonymous' });
  },
}));
